import React, { useState } from "react";
import { Popover } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import FilterPicker from "../dashboard/ChartTargetPicker";

interface FilterSelectProps {
  title: string;
  options: Array<{ key: string; label: string; origin?: string }>;
  selectedKeys: string[];
  onSelectionChange: (selected: string[]) => void;
  showOriginLogic?: boolean;
  /** Note under the popover's title — e.g. a cap on how many may be picked. */
  subtitle?: string;
  // Tighter vertical padding so the trigger lands on 34px (the 24px count
  // badge makes the default ~42px) — for toolbars with 34px controls.
  compact?: boolean;
}

const FilterSelect: React.FC<FilterSelectProps> = ({
  title,
  options,
  selectedKeys,
  onSelectionChange,
  showOriginLogic = false,
  subtitle,
  compact = false,
}) => {
  const theme: Theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <>
      <button
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: compact ? "4px 4px 4px 12px" : "8px 4px 8px 12px",
          borderRadius: "8px",
          border: `1px solid ${
            Boolean(anchorEl)
              ? theme.colors_ds.PRIMARY_ICON_MAIN
              : theme.colors_ds.NEUTRAL_BORDER_WEAK
          }`,
          cursor: "pointer",
          boxSizing: "border-box" as "border-box",
          position: "relative" as "relative",
          zIndex: 1,
          backgroundColor: "white",
          WebkitTapHighlightColor: "transparent",
          outline: "none",
          margin: 0,
        }}
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
          event.stopPropagation();
          setAnchorEl(event.currentTarget);
        }}
        type="button"
      >
        <div
          style={{
            color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
            fontFamily: "var(--font-Medium)",
            fontSize: "12px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <span>{title}</span>
          <div
            style={{
              width: "24px",
              height: "24px",
              backgroundColor: theme.colors_ds.PRIMARY_ICON_MAIN,
              borderRadius: "50%",
              color: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
              fontFamily: "var(--font-Bold)",
              fontSize: "14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              visibility: selectedKeys.length > 0 ? "visible" : "hidden",
            }}
          >
            {selectedKeys.length || 0}
          </div>
        </div>
        <KeyboardArrowDownIcon
          style={{
            transform: Boolean(anchorEl) ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.3s ease",
          }}
        />
      </button>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            marginTop: "8px",
            boxShadow: "var(--box-shadow-general)",
          },
        }}
      >
        <FilterPicker
          data={options.map((option) => option.key)}
          title={title}
          selectedKeys={selectedKeys}
          onSelectionChange={onSelectionChange}
          options={options}
          showOriginLogic={showOriginLogic}
          subtitle={subtitle}
        />
      </Popover>
    </>
  );
};

export default FilterSelect;
